<script setup lang="ts">
import { SharedData } from '@/@types'
import { Coupon } from '@/@types/cart'
import { Order, OrdersnapShot } from '@/@types/order'
import { PaymentHeader, PaymentTitle } from '@/components/shared/checkout/payment-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { centsToDecimalString, formatPrice } from '@/helpers/current-price'
import { Link, router, usePage } from '@inertiajs/vue3'
import { loadScript } from '@paypal/paypal-js'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'
import Resume from '../cart/payment-methods/Resume.vue'
import PaymentStepper from './payment-header/PaymentStepper.vue'

interface OrderWithCoupon extends Order {
    coupon: Coupon
    order_snap_shot: OrdersnapShot
}

const { order } = defineProps<{ order: OrderWithCoupon }>()

const { user } = usePage<SharedData>().props

const { t, locale } = useI18n()

const currentLocale = computed(() => {
    switch (locale.value) {
        case 'pt-BR':
            return 'pt_BR'
        case 'en-US':
            return 'en_US'
        default:
            return 'pt_BR'
    }
})

onMounted(async () => {
    const paypal: any = await loadScript({
        clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID,
        currency: 'BRL',
        locale: currentLocale.value,
    })

    paypal
        .Buttons({
            style: {
                color: 'white',
                label: 'pay',
            },

            fundingSource: paypal.FUNDING.PAYPAL,

            createOrder: async (_data: any, actions: any) => {
                if (order.transaction_detail?.gateway_order_id) {
                    return order.transaction_detail.gateway_order_id
                }

                const gateway_order_id = await actions.order.create({
                    intent: 'CAPTURE',
                    payment_source: {
                        paypal: {
                            experience_context: {
                                landing_page: 'LOGIN',
                                shipping_preference: 'NO_SHIPPING',
                            },
                        },
                    },
                    purchase_units: [
                        {
                            reference_id: order.id,
                            invoice_id: order.id,
                            amount: {
                                currency_code: 'BRL',
                                value: centsToDecimalString(order.order_snap_shot.total_cents),
                                breakdown: {
                                    item_total: {
                                        currency_code: 'BRL',
                                        value: centsToDecimalString(order.order_snap_shot.subtotal_cents),
                                    },
                                    discount: {
                                        currency_code: 'BRL',
                                        value: centsToDecimalString(order.order_snap_shot.discount_cents),
                                    },
                                },
                            },
                            items: order.order_snap_shot.items_data.map((item) => ({
                                name: t(item.ticket_category),
                                unit_amount: {
                                    currency_code: 'BRL',
                                    value: centsToDecimalString(item.original_price_cents),
                                },
                                quantity: item.quantity,
                                category: 'DIGITAL_GOODS',
                            })),
                        },
                    ],
                })

                router.patch(
                    '/paypal/set_gateway_order_id',
                    {
                        order_id: order.id,
                        gateway_order_id: gateway_order_id,
                    },
                    { preserveState: true, only: ['order'] },
                )

                return gateway_order_id
            },

            onApprove: async (_data: any, actions: any) => {
                const details = await actions.order.capture()

                router.post(
                    '/paypal/capture_details',
                    {
                        order_id: order.id,
                        paypal_response: details,
                    },
                    { preserveState: true },
                )
            },

            onError: function (err: string) {
                if (String(err).includes('PAYMENT_ALREADY_DONE')) {
                    toast.info(t('This payment has now been completed and will appear on our website shortly.'))
                } else if (String(err).includes('INSTRUMENT_DECLINED')) {
                    toast.warning(t("The instrument presented was either declined by the processor or bank, or it can't be used for this payment."))
                } else if (String(err).includes('TRANSACTION_REFUSED')) {
                    toast.warning(t('The transaction was refused.'))
                } else if (String(err).includes('INVALID_RESOURCE_ID')) {
                    toast.warning(t('The requested resource ID was not found.'))
                } else {
                    toast.error(t('We were unable to process your payment. Please try again shortly, or contact support.'))
                }
            },
        })
        .render('#paypal-button-container')
})
</script>

<template>
    <main class="container">
        <PaymentHeader :variant="order.status">
            <PaymentTitle :variant="order.status">
                {{ $t('Awaiting payment') }}
                <template #description>
                    {{ $t('Your order') }}
                    <strong> #{{ order.id }} </strong>
                    {{ $t('was placed successfully') }}.
                </template>
            </PaymentTitle>
            <PaymentStepper
                variant="waiting"
                :steps="[
                    {
                        name: $t('Purchase made'),
                        state: 'completed',
                    },
                    {
                        name: $t('Awaiting payment'),
                        state: 'active',
                    },
                    {
                        name: $t('Payment received'),
                        state: 'inactive',
                    },
                ]"
            />
        </PaymentHeader>

        <section class="mt-6 flex flex-col gap-8 lg:flex-row">
            <div class="flex flex-1 flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-8">
                <div class="mx-auto flex max-w-xl flex-col items-center justify-center">
                    <Badge
                        :variant="order.status"
                        class="rounded-sm border-0 before:size-1.5 before:rounded-full before:bg-yellow-400 before:content-['']"
                    >
                        {{ $t(order.status) }}
                    </Badge>

                    <h2 class="mt-4 mb-2.5 text-center font-sans text-2xl font-semibold">
                        {{ $t('Amount to pay:', { amount: formatPrice(order.order_snap_shot.total_cents / 100) }) }}
                    </h2>

                    <p
                        class="mb-8 text-center text-[#444]"
                        v-html="
                            $t(`Once payment is approved, you will receive an email at  with information about the tickets purchased.`, {
                                email: user?.email,
                            })
                        "
                    />
                </div>

                <Separator class="mx-auto my-8 max-w-[612px]" />

                <div class="flex w-6/12 justify-center">
                    <div id="paypal-button-container" class="w-full"></div>
                </div>
                <Button :as="Link" href="?show_order_details=true" class="my-10">{{ $t('View order details') }}</Button>
            </div>
            <Resume :order="order" />
        </section>
    </main>
</template>
